import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { usePrivy } from "@privy-io/react-auth";
import { useState, useTransition } from "react";
import { queryClient, useCreateSquad, useTokenList } from "@/lib/hooks/useApi";
import type { TokenListResponse } from "@/lib/types/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import AnimatedLoading from "@/components/AnimatedLoading";

export const Route = createFileRoute("/(game)/create-squad")({
  component: CreateSquad,
});

// Simplified token search sheet content
interface TokenSearchSheetContentProps {
  onSelectToken: (token: TokenListResponse) => void;
  selectedTokens: TokenListResponse[];
}

function TokenSearchSheetContent({
  onSelectToken,
  selectedTokens,
}: TokenSearchSheetContentProps) {
  const { data: tokenData, isLoading } = useTokenList();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPosition, setSelectedPosition] = useState("");

  const availableTokens = tokenData || [];
  const positionOptions = ["CB", "MF", "ST"];

  // Filter tokens
  let filteredTokens = availableTokens;

  if (searchQuery) {
    filteredTokens = availableTokens.filter(
      (token) =>
        token.tokenName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        token.tokenSymbol.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }

  if (selectedPosition) {
    filteredTokens = filteredTokens.filter(
      (token) => token.position === selectedPosition,
    );
  }

  // Don't remove already selected tokens - allow duplicates

  // Limit to 5 players maximum
  if (selectedTokens.length >= 5) {
    filteredTokens = [];
  }

  return (
    <div className="flex flex-col gap-3 h-full">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-3">
        <h3 className="text-lg font-semibold text-gray-900">
          Select Player ({selectedTokens.length}/5)
        </h3>
        <div className="text-sm text-gray-600">
          {filteredTokens.length} available
        </div>
      </div>

      {/* Search */}
      <Input
        placeholder="Search by name or symbol..."
        className="w-full bg-gray-50 border border-gray-300 rounded-lg py-2.5 px-3 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      {/* Position filter */}
      <div className="flex gap-2">
        {positionOptions.map((pos) => (
          <button
            key={pos}
            onClick={() =>
              setSelectedPosition(selectedPosition === pos ? "" : pos)
            }
            className={cn(
              "px-4 py-2 rounded-lg text-sm font-medium transition-all",
              selectedPosition === pos
                ? "bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200",
            )}
          >
            {pos}
          </button>
        ))}
      </div>

      {/* Token list */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="text-center py-12 text-gray-600">
            Loading players...
          </div>
        ) : selectedTokens.length >= 5 ? (
          <div className="text-center py-12 text-gray-600">
            <p>Squad is complete!</p>
            <p className="text-sm mt-1">You've selected all 5 players</p>
          </div>
        ) : filteredTokens.length === 0 ? (
          <div className="text-center py-12 text-gray-600">
            <p>No players found</p>
            <p className="text-sm mt-1">
              {searchQuery
                ? `Try searching for "${searchQuery}"`
                : selectedPosition
                  ? `No ${selectedPosition} players available`
                  : "No players found"}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {filteredTokens.map((token) => (
              <button
                key={token.pythLazerId}
                onClick={() => onSelectToken(token)}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg py-2.5 px-3 text-left hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-900 text-sm font-medium">
                      {token.tokenName}
                    </p>
                    <p className="text-gray-600 text-xs">{token.tokenSymbol}</p>
                  </div>
                  <div
                    className={cn(
                      "text-xs font-semibold px-2 py-0.5 rounded",
                      token.position === "ST"
                        ? "text-red-700 bg-red-100"
                        : token.position === "MF"
                          ? "text-sky-700 bg-sky-100"
                          : token.position === "CB"
                            ? "text-yellow-700 bg-yellow-100"
                            : "text-emerald-700 bg-emerald-100",
                    )}
                  >
                    {token.position}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CreateSquad() {
  const [selectedTokens, setSelectedTokens] = useState<TokenListResponse[]>([]);
  const [squadName, setSquadName] = useState("");
  const [isMutating, startTransition] = useTransition();
  const { mutateAsync: createSquad } = useCreateSquad();
  const { user } = usePrivy();
  const navigate = useNavigate();

  const handleBack = () => navigate({ to: "/home" });

  const handleSelectToken = (token: TokenListResponse) => {
    setSelectedTokens((prev) => {
      return [...prev, token];
    });
  };

  const handleRemoveToken = (tokenId: number) => {
    setSelectedTokens((prev) =>
      prev.filter((t) => t.pythLazerId !== tokenId),
    );
  };

  const handleCreateSquad = async () => {
    startTransition(async () => {
      if (!user?.wallet?.address) {
        throw new Error("User wallet address not found");
      }

      if (!squadName.trim()) {
        toast.error("Squad name is required", {
          position: "top-center",
        });
        return;
      }

      if (selectedTokens.length === 0) {
        toast.error("Please select at least one player", {
          position: "top-center",
        });
        return;
      }

      try {
        const squadData = {
          name: squadName.trim(),
          formations: "0-0-5", // Default formation for mock
          playerIds: selectedTokens.map((token) => token.pythLazerId),
        };

        // Call createSquad mutation hook
        await createSquad(squadData);

        // invalidate squads query to refresh data
        queryClient.invalidateQueries({ queryKey: ["squads"] });

        toast.success("Squad created successfully!", {
          position: "top-center",
        });

        navigate({ to: "/home" });
      } catch (error: unknown) {
        console.error("Caught error:", error);

        let message = "An unexpected error occurred.";

        if (
          typeof error === "object" &&
          error !== null &&
          "response" in error
        ) {
          const err = error as {
            response?: { data?: { error?: string } };
          };

          message = err.response?.data?.error ?? message;
        }

        toast.error("Create squad failed", {
          description: message,
          position: "top-center",
        });
      }
    });
  };

  return (
    <div className="bg-gray-50 p-4">
      <div className="flex flex-col flex-1">
        {/* Header */}
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Create New Squad
          </h2>
          <p className="text-gray-600 text-lg">
            Build your ultimate crypto team
          </p>
        </div>

        {/* Squad Name Input */}
        <div className="mb-8">
          <label
            htmlFor="squadName"
            className="block text-sm font-medium text-gray-700 mb-3"
          >
            Squad Name
          </label>
          <Input
            id="squadName"
            value={squadName}
            onChange={(e) => setSquadName(e.target.value)}
            placeholder="Enter your squad name..."
            disabled={isMutating}
          />
        </div>

        {/* Players Selection */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-semibold text-gray-900">
                Your Squad
              </h3>
              <div className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">
                {selectedTokens.length}/5
              </div>
            </div>
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  disabled={isMutating || selectedTokens.length >= 5}
                  className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white border-0 shadow-lg"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Player
                </Button>
              </SheetTrigger>
              <SheetContent
                side="bottom"
                className="h-[80vh] max-w-sm mx-auto bg-white border-gray-200"
              >
                <TokenSearchSheetContent
                  onSelectToken={handleSelectToken}
                  selectedTokens={selectedTokens}
                />
              </SheetContent>
            </Sheet>
          </div>

          {selectedTokens.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300">
              <p className="text-gray-700 mb-2 font-medium">
                No players selected
              </p>
              <p className="text-sm text-gray-500">
                Click "Add Player" to start building your dream team
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {selectedTokens.map((token) => (
                <div
                  key={token.pythLazerId}
                  className="flex items-center justify-between px-4 py-2 bg-gray-50 border border-gray-200 rounded-2xl"
                >
                  <div className="flex items-center gap-2">
                    <div>
                      <p className="font-semibold text-gray-900 text-lg">
                        {token.tokenName}
                      </p>
                      <div className="flex items-center gap-1">
                        <p className="text-gray-600 text-sm">
                          {token.tokenSymbol}
                        </p>
                        <span
                          className={cn(
                            "px-2 py-0.5 rounded-md text-xs font-medium",
                            token.position === "ST"
                              ? "text-red-700 bg-red-100"
                              : token.position === "MF"
                                ? "text-sky-700 bg-sky-100"
                                : token.position === "CB"
                                  ? "text-yellow-700 bg-yellow-100"
                                  : "bg-emerald-100 text-emerald-700",
                          )}
                        >
                          {token.position}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveToken(token.pythLazerId)}
                    disabled={isMutating}
                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mt-8">
          <Button
            onClick={handleBack}
            disabled={isMutating}
            className="flex-1 bg-gray-700/50 hover:bg-gray-600/50 text-gray-200 border-gray-600"
          >
            Back
          </Button>
          <Button
            onClick={handleCreateSquad}
            disabled={
              isMutating || selectedTokens.length === 0 || !squadName.trim()
            }
            className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white shadow-lg disabled:opacity-50"
          >
            {isMutating ? <AnimatedLoading/> : "Create Squad"}
          </Button>
        </div>
      </div>
    </div>
  );
}
